import { Component, computed, inject, signal } from '@angular/core';
import { form, FormField, minLength, required } from '@angular/forms/signals';
import { GeoCoordinate, GeofenceKind, GeofenceKindColor, GeofenceV1Request, SlimGeofence } from '@geofences/geofence';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { firstValueFrom } from 'rxjs';

import {
  GeofenceCoordinates,
  GoogleMapsPolygonEditorComponent
} from '@core/components/google-maps-polygon-editor/google-maps-polygon-editor';
import { GeofenceService } from '@core/services/geofence';

interface GeofenceFormModel {
  name: string;
  coordinates: GeoCoordinate[];
}

@Component({
  selector: 'app-create-geofence-form',
  standalone: true,
  templateUrl: './create-geofence-form.html',
  styleUrl: './create-geofence-form.scss',
  imports: [Message, GoogleMapsPolygonEditorComponent, ButtonModule, InputTextModule, FormField]
})
export class CreateGeofenceForm {
  private readonly geofenceApis = inject(GeofenceService);
  private readonly messageService = inject(MessageService);
  private readonly dialogRef = inject(DynamicDialogRef, { optional: true });

  protected readonly loading = signal<boolean>(false);
  private readonly geofenceFormModel = signal<GeofenceFormModel>({
    name: '',
    coordinates: []
  });
  protected readonly geofenceForm = form(this.geofenceFormModel, (form) => {
    required(form.name, { message: 'Geofence Name Required' });
    minLength(form.name, 3, { message: 'Geofence Name Length must be at least 3 characters' });
    required(form.coordinates);
    minLength(form.coordinates, 3);
  });

  protected readonly slimGeofence = computed<SlimGeofence>(() => {
    const { name, coordinates } = this.geofenceForm().value();
    return {
      geofenceId: 'CREATE',
      name,
      coordinates,
      geofenceKind: GeofenceKind.Hub
    };
  });

  protected readonly GeofenceKind = GeofenceKind;
  protected readonly GeofenceKindColor = GeofenceKindColor;

  async onGeofenceCoordinatesChange(geofenceCoordinates: GeofenceCoordinates): Promise<void> {
    console.log('onGeofenceCoordinatesChange', geofenceCoordinates);
    this.geofenceForm.coordinates().controlValue.set(geofenceCoordinates.coordinates);
  }

  async onSubmit(): Promise<void> {
    const { name, coordinates } = this.geofenceForm().value();
    const payload: GeofenceV1Request = {
      name,
      coordinates,
      geofenceKindId: GeofenceKind.Hub
    };

    try {
      const response = await firstValueFrom(this.geofenceApis.create(payload));
      this.closeDialog(response.data.geofenceId);
    } catch (error: any) {
      const errorMessage = error?.error?.message ?? error?.error ?? 'Failed to save geofence';
      this.showError(errorMessage);
    } finally {
      // this.loading.set(false);
    }
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail });
  }

  closeDialog(geofenceId: string | null): void {
    if (this.dialogRef) {
      this.dialogRef.close(geofenceId);
    }
  }

  onCancel(): void {
    this.closeDialog(null);
  }
}
